import React, { ReactNode } from "react";
import pluralize from "pluralize";
import {
  Box,
  Center,
  ComponentWithAs,
  Grid,
  Heading,
  HeadingProps,
  Img,
  Link,
  List,
  ListIcon,
  ListItem,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import { EXAM_RETAKE_WAITING_PERIOD } from "@utils";
import { ExamButton } from "@components/Exam/ExamButton";
import { FiCheckCircle } from "@react-icons/all-files/fi/FiCheckCircle";
import { FiClock } from "@react-icons/all-files/fi/FiClock";
import { FiInfo } from "@react-icons/all-files/fi/FiInfo";
import { FiUserX } from "@react-icons/all-files/fi/FiUserX";
import { Footer } from "@components/Footer";
import { Link as GatsbyLink, PageProps, graphql } from "gatsby";
import { Header } from "@components/Header";
import { PageSeo } from "@components/PageSeo";

function ExamHeading(props: HeadingProps) {
  return <Heading as="h2" size="lg" {...props} />;
}

type ExamListItemProps = {
  children: ReactNode;
  icon: ComponentWithAs<"svg">;
};

function ExamListItem({ icon, children }: ExamListItemProps) {
  return (
    <ListItem d="flex" alignItems="center">
      <ListIcon as={icon} />
      {children}
    </ListItem>
  );
}

export default function ExamDetails({
  data: { exam },
}: PageProps<Queries.GetExamQuery>) {
  if (!exam) {
    return null;
  }

  const {
    slug,
    progressId,
    title,
    description,
    image,
    passingScore,
    topics,
    certification,
  } = exam;

  const { id: certificationId, slug: certificationSlug } = certification;

  const allQuestions = topics.flatMap((topic) => topic.questions);
  const minCorrectAnswers =
    passingScore &&
    allQuestions.length &&
    Math.ceil((allQuestions.length * passingScore) / 100);

  return (
    <>
      <PageSeo title={title ?? ""} description={description ?? undefined} />
      <Header borderBottomWidth={1}>{title} Certification Exam</Header>
      <Grid templateColumns={{ lg: "1fr 2fr" }} as="main" fontSize="lg">
        <Center
          bg="bgSecondary"
          h={{ lg: "100vh" }}
          pos={{ lg: "sticky" }}
          top="0"
          py={{ base: 6, md: 8 }}
        >
          <VStack spacing="6" textAlign="center">
            {image && title && (
              <Img src={image} alt={title} w="168px" h="auto" />
            )}
            <Box>
              <Box
                textTransform="uppercase"
                fontSize="sm"
                fontFamily="mono"
                fontWeight="semibold"
                mb="0.5"
              >
                {title}
              </Box>
              <Heading size="xl" as="h1">
                Certification Exam
              </Heading>
            </Box>
            <ExamButton
              examSlug={slug}
              testId={progressId}
              certificationId={certificationId}
            />
          </VStack>
        </Center>
        <VStack spacing="8" p="10" alignItems="flex-start">
          <Box>
            <ExamHeading>Exam details</ExamHeading>

            <List spacing="2" mt="3">
              {/* TODO: use time limit from frontmatter */}
              <ExamListItem icon={FiClock}>
                After you start this exam, you&apos;ll have 24 hours to complete
                it
              </ExamListItem>
              <ExamListItem icon={FiUserX}>Unproctored</ExamListItem>
              <ExamListItem icon={FiInfo}>
                {allQuestions.length} multiple choice{" "}
                {pluralize("question", allQuestions.length)}
              </ExamListItem>
              {minCorrectAnswers && (
                <ExamListItem icon={FiCheckCircle}>
                  {minCorrectAnswers} correct{" "}
                  {pluralize("answer", minCorrectAnswers)} required to pass
                </ExamListItem>
              )}
            </List>
          </Box>

          <Box>
            <ExamHeading>Topics covered</ExamHeading>
            <UnorderedList mt="3" mb="27px" spacing="1">
              {topics.map((topic, i) => (
                <ListItem key={i}>{topic.title}</ListItem>
              ))}
            </UnorderedList>
            For a more detailed view, please take a look at{" "}
            <Link
              as={GatsbyLink}
              to={`/certifications/${certificationSlug}/learning-path`}
              color="primary"
              fontWeight="semibold"
            >
              our certification&apos;s learning path.
            </Link>
          </Box>

          <Box maxW="70ch">
            <ExamHeading mb="3">Tips, notes, and recommendations</ExamHeading>
            <Text mb="27px">
              Here are a few tips we recommend to maximize your chances of
              success with the certification exam.
            </Text>

            <UnorderedList spacing="1">
              <ListItem>
                Feel free to review our courses and other materials as much as
                you&apos;d like. The topics covered in this exam are related to
                the concepts taught in the Odyssey tutorials.
              </ListItem>
              <ListItem>
                Make sure your internet connection is stable and ready to go.
              </ListItem>
              <ListItem>
                Find a comfortable spot to take the exam from.
              </ListItem>
              <ListItem>Take your time to read the questions closely.</ListItem>
              <ListItem>
                Some questions may have multiple correct answers.
              </ListItem>
              <ListItem>
                Answer questions in the order that works best for you. As you
                complete the exam, you&apos;ll be able to skip questions and
                come back to them once you&apos;re ready.
              </ListItem>
              <ListItem>
                If you don&apos;t get enough correct questions to meet the
                certification criteria, that&apos;s okay! You&apos;ll have
                opportunities to retake the test after{" "}
                {EXAM_RETAKE_WAITING_PERIOD} days. We&apos;ll also share the
                points and topics we recommend reviewing based on your score.
              </ListItem>
            </UnorderedList>
          </Box>
        </VStack>
      </Grid>

      <Footer />
    </>
  );
}

export const pageQuery = graphql`
  query GetExam($id: String!) {
    exam(id: { eq: $id }) {
      id
      title
      slug
      progressId
      passingScore
      image
      description
      certificationId
      certification {
        id
        slug
      }
      topics {
        id
        slug
        title
        questions {
          id
          slug
        }
      }
    }
  }
`;
